import { ArrowLeftOutlined, SaveOutlined } from "@ant-design/icons"
import { Button, Card, Form, Input, message, Skeleton, Space } from "antd"
import { useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { getOrderResults, updateOrderResult } from "../../endpoints"
import { OrderResultInterface } from "../../endpoints/orderEndpoints"

const { TextArea } = Input

export function EditOrderResult(): JSX.Element {
    const navigate = useNavigate()
    const { orderResultId } = useParams()
    const queryClient = useQueryClient()
    const [messageApi, contextHolder] = message.useMessage()
    const [form] = Form.useForm()
    const [orderResult, setOrderResult] = useState<OrderResultInterface | null>(null)

    // Fetch order result details
    // We need to get the orderId from the order result, so first we fetch by id
    // Since we don't have a direct endpoint to get a single result, we'll fetch by orderId once we have it
    const {
        data: orderResultsData,
        isPending: isOrderResultsPending,
        isError: isOrderResultsError,
    } = useQuery({
        queryKey: ["getOrderResult", orderResultId],
        queryFn: async () => {
            // We need to fetch order results by orderId, but we need to find the orderId first
            // For now, we'll use a workaround - fetch from the previous page context
            // In a real scenario, we'd have a getOrderResultById endpoint
            const params = new URLSearchParams(window.location.search)
            const orderId = params.get('orderId')
            if (!orderId) {
                throw new Error("Order ID not provided")
            }
            return getOrderResults({ orderId: parseInt(orderId) })
        },
        refetchOnMount: "always",
        enabled: !!orderResultId,
    })

    useEffect(() => {
        if (orderResultsData?.code === 1000 && orderResultsData.results) {
            const result = orderResultsData.results.find(
                (r: OrderResultInterface) => r.id === parseInt(orderResultId ?? "0")
            )
            if (result) {
                setOrderResult(result)
                form.setFieldsValue({
                    notes: result.notes || "",
                })
            }
        }
    }, [orderResultsData, orderResultId, form])

    // Update order result mutation
    const updateOrderResultMutation = useMutation({
        mutationFn: updateOrderResult,
        onSuccess: (data) => {
            if (data.code === 1000) {
                messageApi.success("Order result updated successfully!")
                // Invalidate cache to refresh data
                queryClient.invalidateQueries({ queryKey: ["getOrderResults"] })
                queryClient.invalidateQueries({ queryKey: ["getOrderResult"] })
                // Navigate back to the order detail page
                const params = new URLSearchParams(window.location.search)
                const orderId = params.get('orderId')
                if (orderId) {
                    navigate(`/admin/order/${orderId}`)
                }
            } else {
                messageApi.error(data.message || "Failed to update order result")
            }
        },
        onError: (error) => {
            messageApi.error("Failed to update order result")
            console.error("Update error:", error)
        },
    })

    const handleSubmit = (values: any) => {
        updateOrderResultMutation.mutate({
            orderResultId: parseInt(orderResultId ?? "0"),
            notes: values.notes,
        })
    }

    const handleCancel = () => {
        const params = new URLSearchParams(window.location.search)
        const orderId = params.get('orderId')
        if (orderId) {
            navigate(`/admin/order/${orderId}`)
        } else {
            navigate(-1)
        }
    }

    if (isOrderResultsPending) {
        return (
            <div style={{ padding: "24px", maxWidth: "1200px", margin: "0 auto" }}>
                {contextHolder}
                <Skeleton active paragraph={{ rows: 10 }} />
            </div>
        )
    }

    if (isOrderResultsError || !orderResult) {
        return (
            <div style={{ padding: "24px", maxWidth: "1200px", margin: "0 auto" }}>
                {contextHolder}
                <Card>
                    <div style={{ textAlign: "center", padding: "40px" }}>
                        <h2>Order Result Not Found</h2>
                        <p>The order result you're looking for doesn't exist.</p>
                        <Button onClick={handleCancel}>Go Back</Button>
                    </div>
                </Card>
            </div>
        )
    }

    return (
        <div style={{ padding: "24px", maxWidth: "1200px", margin: "0 auto" }}>
            {contextHolder}
            <Card
                title={
                    <Space>
                        <Button 
                            icon={<ArrowLeftOutlined />} 
                            onClick={handleCancel}
                            type="text"
                        />
                        <span>Edit Order Result #{orderResult.id}</span>
                    </Space>
                }
            >
                <div style={{ marginBottom: "24px" }}>
                    <Space direction="vertical" size="small">
                        <div>
                            <strong>Order ID:</strong> {orderResult.orderid}
                        </div>
                        {orderResult.jsonfile && (
                            <div>
                                <strong>JSON File:</strong> {orderResult.jsonfile}
                            </div>
                        )}
                        {orderResult.zipfile && (
                            <div>
                                <strong>ZIP File:</strong> {orderResult.zipfile}
                            </div>
                        )}
                        <div>
                            <strong>Created:</strong> {new Date(orderResult.ts).toLocaleString()}
                        </div>
                    </Space>
                </div>

                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    style={{ marginTop: "24px" }}
                >
                    <Form.Item
                        label="Notes"
                        name="notes"
                        rules={[
                            { max: 5000, message: "Notes cannot exceed 5000 characters" }
                        ]}
                    >
                        <TextArea
                            rows={8}
                            placeholder="Enter notes about this order result..."
                            showCount
                            maxLength={5000}
                        />
                    </Form.Item>

                    <Form.Item>
                        <Space>
                            <Button
                                type="primary"
                                htmlType="submit"
                                icon={<SaveOutlined />}
                                loading={updateOrderResultMutation.isPending}
                            >
                                Save Changes
                            </Button>
                            <Button onClick={handleCancel}>
                                Cancel
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Card>
        </div>
    )
}
